import { useMemo, useState } from "react";
import { Header } from "@/components/ui/header";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown } from "lucide-react";
import {
  CPU_SIZING_DATA,
  CPU_CORES,
  AWS_TIERS,
  AWS_QTY,
  DAYS_MONTH,
  OTHER_OPEX,
  DEFAULTS,
  DESCRIPTIONS,
  MODEL_ORDER,
  USECASE_ORDER,
  type CpuSizingRow,
} from "@/lib/cpuSizingData";

/* ------------------------------- formatting -------------------------------- */
const money = (v: number, d = 0) =>
  Number.isFinite(v)
    ? v.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";
const num = (v: number, d = 0) =>
  Number.isFinite(v)
    ? v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })
    : "—";
const pct = (v: number | null) =>
  v != null && Number.isFinite(v) ? (v * 100).toFixed(1) + "%" : "—";
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/* --------------------------------- model ---------------------------------- */
interface Assumptions {
  activeHours: number;
  lifecycle: number;
  electricity: number;
  pue: number;
}

function awsForCpu(cpu: string) {
  const cores = CPU_CORES[cpu] ?? 128;
  const tier = cores <= 64 ? AWS_TIERS.x8i : AWS_TIERS.r8i;
  return {
    ...tier,
    cores,
    onDemandPair: tier.onDemand * AWS_QTY,
    sp3Pair: tier.sp3NoUpfront * AWS_QTY,
  };
}

const rowKey = (r: { platform: string; cpu: string }) => `${r.platform}|${r.cpu}`;

function calc(r: CpuSizingRow, s: Assumptions & { users: number }) {
  const aws = awsForCpu(r.cpu);
  const monthlyHours = s.activeHours * DAYS_MONTH;
  const monthlyCapex = r.packageCost / (s.lifecycle * 12);
  const energy = (r.loadPower / 1000) * monthlyHours * s.electricity * s.pue;
  const monthlyAllIn = monthlyCapex + energy + OTHER_OPEX;
  const outputM = (r.serving * 3600 * monthlyHours) / 1e6;
  const onPrem = outputM > 0 ? monthlyAllIn / outputM : NaN;
  const cloudOutputPerHr = (r.serving * 3600) / 1e6;
  const cloudPerMOD = cloudOutputPerHr > 0 ? aws.onDemandPair / cloudOutputPerHr : NaN;
  const cloudPerMSP = cloudOutputPerHr > 0 ? aws.sp3Pair / cloudOutputPerHr : NaN;
  const monthlyCloudOD = aws.onDemandPair * monthlyHours;
  const monthlyCloudSP = aws.sp3Pair * monthlyHours;
  const cashOpex = energy + OTHER_OPEX;
  const breakEvenOD =
    monthlyCloudOD > cashOpex ? r.packageCost / (monthlyCloudOD - cashOpex) : null;
  const breakEvenSP =
    monthlyCloudSP > cashOpex ? r.packageCost / (monthlyCloudSP - cashOpex) : null;
  const months = s.lifecycle * 12;
  const lifeOnPrem = r.packageCost + cashOpex * months;
  const lifeSavingsOD = monthlyCloudOD * months - lifeOnPrem;
  const lifeSavingsSP = monthlyCloudSP * months - lifeOnPrem;
  const savingsPctOD =
    cloudPerMOD > 0 && Number.isFinite(onPrem) ? (cloudPerMOD - onPrem) / cloudPerMOD : null;
  const savingsPctSP =
    cloudPerMSP > 0 && Number.isFinite(onPrem) ? (cloudPerMSP - onPrem) / cloudPerMSP : null;
  return {
    ...r,
    aws,
    onPrem,
    cloudPerMOD,
    cloudPerMSP,
    breakEvenOD,
    breakEvenSP,
    lifeSavingsOD,
    lifeSavingsSP,
    savingsPctOD,
    savingsPctSP,
    fit: r.maxUsers >= s.users,
    headroom: r.maxUsers - s.users,
    rank: null as number | null,
  };
}
type Computed = ReturnType<typeof calc>;

/* --------------------------------- view ---------------------------------- */
const KPI = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
    <div className="text-xs font-semibold text-lenovo-gray">{label}</div>
    <div className="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-lenovo-gray">{sub}</div>}
  </div>
);

const Bar = ({ label, value, max, color }: { label: string; value: number; max: number; color: string }) => (
  <div className="flex-1 flex flex-col items-center justify-end">
    <div className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1.5">{money(value, 2)}</div>
    <div
      className={`w-full rounded-t ${color}`}
      style={{ height: `${25 + 150 * (max > 0 ? Math.max(0, value) / max : 0)}px` }}
    />
    <div className="mt-2 text-xs font-semibold text-gray-600 dark:text-gray-400">{label}</div>
  </div>
);

const fieldLabel = "block text-sm font-semibold mb-1.5 text-gray-800 dark:text-gray-200";
const note = "mt-1.5 text-xs text-lenovo-gray";

export default function CpuSizing() {
  const [usecase, setUsecase] = useState(DEFAULTS.usecase);
  const [model, setModel] = useState(DEFAULTS.model);
  const [users, setUsers] = useState<number>(DEFAULTS.users);
  const [advOpen, setAdvOpen] = useState(false);
  const [activeHours, setActiveHours] = useState<number>(DEFAULTS.activeHours);
  const [lifecycle, setLifecycle] = useState<number>(DEFAULTS.lifecycle);
  const [electricity, setElectricity] = useState<number>(DEFAULTS.electricity);
  const [pue, setPue] = useState<number>(DEFAULTS.pue);
  const [fitOnly, setFitOnly] = useState(true);
  const [choiceKey, setChoiceKey] = useState<string | null>(null);

  const s = {
    users: Math.max(1, users || 1),
    activeHours: clamp(activeHours || 24, 0.1, 24),
    lifecycle: Math.max(1, lifecycle || 5),
    electricity: Math.max(0, electricity || 0),
    pue: Math.max(1, pue || 1),
  };

  const { scenario, eligible } = useMemo(() => {
    const rows: Computed[] = CPU_SIZING_DATA.filter(
      (r) => r.model === model && r.usecase === usecase,
    ).map((r) => calc(r, s));
    const elig = rows
      .filter((r) => r.fit)
      .sort((a, b) => a.onPrem - b.onPrem || a.packageCost - b.packageCost);
    elig.forEach((r, i) => (r.rank = i + 1));
    return { scenario: rows, eligible: elig };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, usecase, s.users, s.activeHours, s.lifecycle, s.electricity, s.pue]);

  const choiceRows = useMemo(
    () =>
      eligible
        .slice()
        .sort(
          (a, b) =>
            a.packageCost - b.packageCost ||
            a.platform.localeCompare(b.platform) ||
            a.cpu.localeCompare(b.cpu),
        ),
    [eligible],
  );

  const selected: Computed | undefined = eligible.length
    ? eligible.find((r) => rowKey(r) === choiceKey) ?? choiceRows[0]
    : scenario.slice().sort((a, b) => b.maxUsers - a.maxUsers)[0];

  const fits = !!selected && eligible.length > 0 && selected.fit;
  const barMax = selected
    ? Math.max(selected.onPrem || 0, selected.cloudPerMOD || 0, selected.cloudPerMSP || 0, 0.01)
    : 1;

  const tableRows = scenario
    .filter((r) => !fitOnly || r.fit)
    .sort((a, b) => {
      if (a.fit !== b.fit) return a.fit ? -1 : 1;
      if (a.fit && b.fit) return (a.rank ?? 0) - (b.rank ?? 0);
      return b.maxUsers - a.maxUsers;
    });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">CPU AI Sizing &amp; Token Economics</h1>
          <p className="text-sm text-lenovo-gray mt-1">
            Lenovo Xeon 6 CPU serving capacity with an automatic AWS Xeon 6 cloud cost comparison.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* ------------------------------- inputs ------------------------------- */}
          <div className="w-full lg:w-1/3">
            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="text-xs font-bold text-lenovo-red">
                  Start with the customer
                </div>
                <h2 className="text-lg font-semibold mt-1 mb-1">What does the business need?</h2>
                <p className="text-xs text-lenovo-gray mb-4">
                  Pick the workload, expected users, and model size. AWS Xeon 6 pricing updates automatically.
                </p>

                <div className="my-4">
                  <label className={fieldLabel}>Business use case</label>
                  <Select value={usecase} onValueChange={setUsecase}>
                    <SelectTrigger className="focus:ring-lenovo-red">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {USECASE_ORDER.map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className={note}>{DESCRIPTIONS.usecase[usecase]}</div>
                </div>

                <div className="my-4">
                  <label className={fieldLabel}>Model size</label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger className="focus:ring-lenovo-red">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_ORDER.map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className={note}>{DESCRIPTIONS.model[model]}</div>
                </div>

                <div className="my-4">
                  <label className={fieldLabel}>Concurrent users required</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={1}
                      max={181}
                      step={1}
                      value={clamp(s.users, 1, 181)}
                      onChange={(e) => setUsers(+e.target.value)}
                      className="flex-1"
                      style={{ accentColor: "var(--lenovo-red)" }}
                    />
                    <Input
                      type="number"
                      min={1}
                      max={999}
                      value={users}
                      onChange={(e) => setUsers(parseInt(e.target.value) || 1)}
                      className="w-20 focus-visible:ring-lenovo-red"
                    />
                  </div>
                  <div className={note}>Capacity uses the benchmark SLA (TTFT &lt; 1s, TPOT &lt; 100 ms).</div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 my-4">
                  <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Automatic AWS Xeon 6 comparison
                  </div>
                  <div className="text-xs text-lenovo-gray leading-relaxed">
                    US East (N. Virginia) · Linux · shared tenancy · 1 TiB RAM per instance ·
                    On-Demand and 3-Year EC2 Instance Savings Plan (No Upfront) · two AWS instances per Lenovo server.
                  </div>
                </div>

                <Collapsible open={advOpen} onOpenChange={setAdvOpen}>
                  <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
                    <ChevronDown className={`w-4 h-4 transition-transform ${advOpen ? "rotate-180" : ""}`} />
                    Advanced assumptions
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2 grid grid-cols-2 gap-3">
                    <div>
                      <label className={fieldLabel}>Active hours/day</label>
                      <Input type="number" min={0.1} max={24} step={0.1} value={activeHours}
                        onChange={(e) => setActiveHours(parseFloat(e.target.value))}
                        className="focus-visible:ring-lenovo-red" />
                    </div>
                    <div>
                      <label className={fieldLabel}>Lifecycle (years)</label>
                      <Input type="number" min={1} max={10} step={1} value={lifecycle}
                        onChange={(e) => setLifecycle(parseInt(e.target.value))}
                        className="focus-visible:ring-lenovo-red" />
                    </div>
                    <div>
                      <label className={fieldLabel}>Electricity ($/kWh)</label>
                      <Input type="number" min={0} step={0.01} value={electricity}
                        onChange={(e) => setElectricity(parseFloat(e.target.value))}
                        className="focus-visible:ring-lenovo-red" />
                    </div>
                    <div>
                      <label className={fieldLabel}>PUE</label>
                      <Input type="number" min={1} step={0.05} value={pue}
                        onChange={(e) => setPue(parseFloat(e.target.value))}
                        className="focus-visible:ring-lenovo-red" />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          </div>

          {/* ------------------------------- results ------------------------------ */}
          <div className="w-full lg:w-2/3 space-y-6">
            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="text-xs font-bold text-lenovo-red">
                      Selected configuration
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-lenovo-gray mb-1">Choose server / CPU</label>
                      <Select
                        value={eligible.length ? rowKey(selected!) : ""}
                        onValueChange={setChoiceKey}
                        disabled={!eligible.length}
                      >
                        <SelectTrigger className="focus:ring-lenovo-red font-semibold">
                          <SelectValue placeholder="No single-server option fits this requirement" />
                        </SelectTrigger>
                        <SelectContent>
                          {choiceRows.map((r) => (
                            <SelectItem key={rowKey(r)} value={rowKey(r)}>
                              {r.platform} · Xeon {r.cpu} — {num(r.maxUsers)} users — {money(r.packageCost)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <h2 className="text-xl font-bold mt-3">
                      {selected ? `${selected.platform} · Xeon ${selected.cpu}` : "—"}
                    </h2>
                    {selected && <div className="text-xs text-lenovo-gray mt-0.5">{selected.basis}</div>}
                  </div>
                  <Badge
                    className={
                      fits
                        ? "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/40"
                        : "bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/40"
                    }
                  >
                    {fits ? "Fits demand" : "No single-server fit"}
                  </Badge>
                </div>

                {selected && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                      <KPI
                        label="Concurrent capacity"
                        value={`${num(selected.maxUsers)} users`}
                        sub={
                          fits
                            ? `${num(selected.headroom)} users headroom`
                            : `short by ${num(Math.abs(selected.headroom))} users`
                        }
                      />
                      <KPI label="Lenovo package price" value={money(selected.packageCost)} sub="Planning basis" />
                      <KPI label="On-prem token cost" value={money(selected.onPrem, 2)} sub="$/1M output tokens" />
                      <KPI
                        label="AWS On-Demand"
                        value={money(selected.cloudPerMOD, 2)}
                        sub={`${money(selected.aws.onDemandPair, 2)}/hr pair`}
                      />
                      <KPI
                        label="AWS 3-Year"
                        value={money(selected.cloudPerMSP, 2)}
                        sub={`${money(selected.aws.sp3Pair, 2)}/hr pair`}
                      />
                      <KPI
                        label="Cash breakeven"
                        value={selected.breakEvenOD != null ? `${num(selected.breakEvenOD, 1)} mo` : "—"}
                        sub={selected.breakEvenSP != null ? `${num(selected.breakEvenSP, 1)} mo vs 3-Year` : undefined}
                      />
                    </div>

                    <div className="mt-4 border-l-4 border-lenovo-red bg-red-50 dark:bg-red-950/30 rounded-r-lg px-3.5 py-3 text-sm">
                      {fits ? (
                        <>
                          <b>Customer-ready takeaway:</b> One {selected.platform} with Xeon {selected.cpu} serves up
                          to <b>{num(selected.maxUsers)} concurrent users</b>. Modeled on-prem cost is{" "}
                          <b>{money(selected.onPrem, 2)}/1M</b> output tokens vs{" "}
                          <b>{money(selected.cloudPerMOD, 2)}/1M</b> AWS On-Demand
                          {selected.savingsPctOD != null && (
                            <> ({pct(selected.savingsPctOD)} lower)</>
                          )}{" "}
                          and <b>{money(selected.cloudPerMSP, 2)}/1M</b> AWS 3-Year.
                        </>
                      ) : (
                        <>
                          <b>Capacity alert:</b> no single-server option reaches {s.users} concurrent users for{" "}
                          {model} / {usecase}. Highest available capacity is {num(selected.maxUsers)} users — consider a
                          multi-server design.
                        </>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {selected && (
              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <CardContent className="p-6">
                  <div className="text-lg font-semibold">Cost per 1M output tokens</div>
                  <div className="text-xs text-lenovo-gray mb-4">
                    On-prem vs two matched AWS Xeon 6 instances.
                  </div>
                  <div className="flex gap-5 items-end px-4 pb-2" style={{ height: 220 }}>
                    <Bar label="On-Prem" value={selected.onPrem || 0} max={barMax} color="bg-lenovo-red" />
                    <Bar label="AWS On-Demand" value={selected.cloudPerMOD || 0} max={barMax} color="bg-gray-400" />
                    <Bar label="AWS 3-Year" value={selected.cloudPerMSP || 0} max={barMax} color="bg-blue-500" />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <div className="text-xs font-bold text-lenovo-red">
                      All CPU choices
                    </div>
                    <h2 className="text-lg font-semibold">Options for this scenario</h2>
                    <div className="text-xs text-lenovo-gray">
                      {eligible.length
                        ? `${eligible.length} of ${scenario.length} configurations meet ${s.users} concurrent users.`
                        : `No single-server configuration meets ${s.users} concurrent users.`}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-lenovo-gray cursor-pointer">
                    <Checkbox
                      checked={fitOnly}
                      onCheckedChange={(v) => setFitOnly(!!v)}
                      className="data-[state=checked]:bg-lenovo-red data-[state=checked]:border-lenovo-red"
                    />
                    Show only options that fit
                  </label>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Platform / CPU</TableHead>
                        <TableHead className="text-right">Max users</TableHead>
                        <TableHead className="text-right">Server price</TableHead>
                        <TableHead className="text-right">On-Prem $/1M</TableHead>
                        <TableHead className="text-right">AWS OD $/1M</TableHead>
                        <TableHead className="text-right">AWS 3-Yr $/1M</TableHead>
                        <TableHead className="text-right">OD savings</TableHead>
                        <TableHead className="text-right">Rank</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tableRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-lenovo-gray py-6">
                            No option meets this user target. Uncheck the filter to see the highest-capacity
                            alternatives.
                          </TableCell>
                        </TableRow>
                      )}
                      {tableRows.map((r) => {
                        const isSel = selected && rowKey(r) === rowKey(selected);
                        return (
                          <TableRow
                            key={rowKey(r)}
                            className={isSel ? "bg-red-50 dark:bg-red-950/30" : undefined}
                          >
                            <TableCell className="font-semibold whitespace-nowrap">
                              {r.platform} · {r.cpu}
                            </TableCell>
                            <TableCell className="text-right">{num(r.maxUsers)}</TableCell>
                            <TableCell className="text-right">{money(r.packageCost)}</TableCell>
                            <TableCell className="text-right">{money(r.onPrem, 2)}</TableCell>
                            <TableCell className="text-right">{money(r.cloudPerMOD, 2)}</TableCell>
                            <TableCell className="text-right">{money(r.cloudPerMSP, 2)}</TableCell>
                            <TableCell className="text-right">{pct(r.savingsPctOD)}</TableCell>
                            <TableCell className="text-right font-bold text-lenovo-red">{r.rank ?? "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <p className="text-xs text-lenovo-gray mt-3 leading-relaxed">
                  On-prem $/1M amortizes server CAPEX over the lifecycle plus modeled electricity at the selected PUE.
                  Cloud $/1M = AWS $/hour ÷ serving output/hour, with cloud throughput normalized to the Lenovo
                  server. SKUs marked estimated should be validated on hardware before an external SLA claim.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
